import React, { forwardRef, useId, useState } from "react";

import { cn } from "src/shadcn/lib/utils";
import {
  Field,
  FieldLabel,
  FieldContent,
  FieldDescription,
  FieldError,
} from "src/primitives/Field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  InputGroupInput,
} from "src/primitives/InputGroup";
import { withIconSize, type IconLike } from "src/utils/withIconSize";

import { SIZE_CONFIG } from "./constants";
import {
  formatWithPrecision,
  enforceDecimalPrecision,
  formatWithRejectCharsRegex,
  getTrimmedValue,
  preserveCursor,
} from "./utils";

type InputSize = "small" | "medium" | "large";

export interface InputProps extends Omit<
  React.ComponentProps<"input">,
  "size" | "prefix" | "value"
> {
  /** Controlled value. */
  value?: string | number;
  /** Size of the input. */
  size?: InputSize;
  /** Input type. */
  type?: string;
  /** Label displayed above the input. */
  label?: string;
  /** Error message displayed below the input. */
  error?: string;
  /** Help text displayed below the input. Accepts string or ReactNode. */
  helpText?: React.ReactNode;
  /** Content rendered before the input. Accepts an element, a component
   * reference (e.g. `Search` from lucide-react), or any other React node. */
  prefix?: IconLike;
  /** Content rendered after the input. Accepts an element, a component
   * reference (e.g. `Search` from lucide-react), or any other React node. */
  suffix?: IconLike;
  /** Removes borders from the input. */
  nakedInput?: boolean;
  /** HTML size attribute for the input element. */
  contentSize?: number;
  /** Maximum character limit. Counter visible at 85%. */
  maxLength?: number;
  /** Allow typing past maxLength, show error styling on count. */
  unlimitedChars?: boolean;
  /** Regex to reject matching characters from input. */
  rejectCharsRegex?: RegExp;
  /** Prevent trimming whitespace on blur. */
  disableTrimOnBlur?: boolean;
  /** Decimal places for number formatting. -1 to disable. */
  precision?: number;
  /** Props forwarded to the Label element. */
  labelProps?: React.ComponentProps<typeof FieldLabel>;
  /** Additional class name for the outermost wrapper. */
  className?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = "medium",
      type = "text",
      label = "",
      error = "",
      helpText = "",
      prefix = null,
      suffix = null,
      disabled = false,
      nakedInput = false,
      contentSize,
      required = false,
      maxLength,
      unlimitedChars = false,
      rejectCharsRegex,
      disableTrimOnBlur = false,
      precision = -1,
      labelProps,
      className = "",
      onBlur,
      onChange,
      value: controlledValue,
      ...otherProps
    },
    ref
  ) => {
    const generatedId = useId();
    const id = otherProps.id ?? generatedId;
    const errorId = `error_${id}`;
    const helpTextId = `helpText_${id}`;

    // Controlled / uncontrolled value tracking
    const [valueInternal, setValueInternal] = useState(controlledValue);
    const isControlled = controlledValue !== undefined;

    const value =
      formatWithPrecision(controlledValue, precision) ?? valueInternal ?? "";
    const valueLength = value?.toString().length || 0;

    const isMaxLengthPresent = !!maxLength || maxLength === 0;
    const isCharacterLimitVisible =
      isMaxLengthPresent && valueLength >= maxLength! * 0.85;
    const maxLengthError = unlimitedChars && valueLength > maxLength!;

    // --- Handlers ---

    const onChangeInternal = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setValueInternal(e.target.value);
      }
      onChange?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (type === "file") {
        onChangeInternal(e);

        return;
      }

      let formattedValue = formatWithRejectCharsRegex(
        e.target.value,
        rejectCharsRegex
      );
      formattedValue = enforceDecimalPrecision(formattedValue, precision);

      if (formattedValue !== e.target.value) {
        preserveCursor(e, () => {
          const syntheticEvent = Object.create(e, {
            target: { value: { ...e.target, value: formattedValue } },
          });
          e.target.value = formattedValue;
          onChangeInternal(syntheticEvent);

          return syntheticEvent;
        });
      } else {
        onChangeInternal(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (type === "file") {
        onBlur?.(e);

        return;
      }

      const trimmedValue = getTrimmedValue(value, disableTrimOnBlur);
      const formattedValue = formatWithPrecision(trimmedValue, precision);

      if (formattedValue !== undefined && formattedValue !== value) {
        e.target.value = formattedValue.toString();
        handleChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
      }

      onBlur?.(e);
    };

    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      if (type === "number") (e.target as HTMLInputElement).blur();
    };

    // --- aria-describedby ---

    const ariaDescribedBy =
      [error ? errorId : null, helpText ? helpTextId : null]
        .filter(Boolean)
        .join(" ") || undefined;

    // --- Render ---

    const hasField = !!(label || error || helpText);
    const sizeConfig = SIZE_CONFIG[size];

    const inputGroup = (
      <InputGroup
        className={cn(
          sizeConfig.group,
          nakedInput &&
            "border-0 shadow-none has-[[data-slot=input-group-control]:focus-visible]:ring-0 has-[[data-slot=input-group-control]:focus-visible]:border-transparent",
          !!error &&
            !nakedInput &&
            "border-destructive ring-destructive/20 ring-3",
          !hasField && className
        )}
      >
        {prefix && (
          <InputGroupAddon
            align="inline-start"
            className={sizeConfig.addonStart}
          >
            <InputGroupText>
              {withIconSize(prefix, sizeConfig.icon)}
            </InputGroupText>
          </InputGroupAddon>
        )}
        <InputGroupInput
          ref={ref}
          id={id}
          type={type}
          disabled={disabled}
          required={required}
          size={contentSize}
          aria-invalid={!!error || undefined}
          aria-describedby={ariaDescribedBy}
          className={cn(sizeConfig.input, nakedInput && "px-0")}
          value={value}
          onBlur={handleBlur}
          onChange={handleChange}
          onWheel={handleWheel}
          {...(isMaxLengthPresent && !unlimitedChars ? { maxLength } : {})}
          {...otherProps}
        />
        {suffix && (
          <InputGroupAddon align="inline-end" className={sizeConfig.addonEnd}>
            <InputGroupText>
              {withIconSize(suffix, sizeConfig.icon)}
            </InputGroupText>
          </InputGroupAddon>
        )}
      </InputGroup>
    );

    if (!hasField) {
      return inputGroup;
    }

    return (
      <Field
        data-disabled={disabled || undefined}
        data-invalid={!!error || undefined}
        className={className}
      >
        {(label || isCharacterLimitVisible) && (
          <div className="flex items-center justify-between">
            {label && (
              <FieldLabel htmlFor={id} {...labelProps}>
                {label}
                {required && (
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                )}
              </FieldLabel>
            )}
            {isCharacterLimitVisible && (
              <span
                className={cn(
                  "text-xs tabular-nums",
                  maxLengthError ? "text-destructive" : "text-muted-foreground"
                )}
              >
                {valueLength}/{maxLength}
              </span>
            )}
          </div>
        )}
        <FieldContent>
          {inputGroup}
          {!!error && <FieldError id={errorId}>{error}</FieldError>}
          {helpText && (
            <FieldDescription id={helpTextId}>{helpText}</FieldDescription>
          )}
        </FieldContent>
      </Field>
    );
  }
);

Input.displayName = "Input";

export { Input };
